import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '@abp/ng.core';
import { faro } from '@grafana/faro-web-sdk';

/** ABP maps AbpDbConcurrencyException to 409 with this message. Matched as a substring. */
export const CONCURRENCY_ERROR_FRAGMENT = 'has already been changed';

@Injectable()
export class CustomErrorInterceptor implements HttpInterceptor {

  constructor(
    public readonly localization: LocalizationService
  ){

  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Report the failing endpoint before the body is rewritten below, so the breadcrumb keeps
        // the raw server message. Concurrency 409s are otherwise indistinguishable in Faro.
        this.reportToFaro(req, error);

        if (error?.error?.error) {
          // Map message
          error.error.error.message = this.mapErrorMessage(error.error.error.message);
        }
        return throwError(() => error);
      })
    );
  }

  private reportToFaro(req: HttpRequest<any>, error: HttpErrorResponse): void {
    try {
      faro?.api?.pushEvent('http_error', {
        method: req.method,
        url: req.urlWithParams,
        status: String(error?.status ?? 0),
        serverMessage: error?.error?.error?.message ?? '',
        serverCode: error?.error?.error?.code ?? '',
      });
    } catch {
      // Faro is optional (disabled per environment) and must never break the error pipeline.
    }
  }

  private mapErrorMessage(message: string): string {
    if (!message) {
      return message;
    }

    const errorMap: { [key: string]: string } = {
      'The End Date should not be less': this.localization.instant("::EndDateShoultNotBeLess") ,
      'Badge or Document Not Found': this.localization.instant("::Badge or Document Not Found"),
      'name is using in another': this.localization.instant("::NameAlreadyUsed"),
      'The attendee has already the same training assigned.': this.localization.instant("::AttendeeAlreadyRegistered"),
      'The attendee has already the same training as instructor.': this.localization.instant("::AttendeeAlreadyAsInstructor"),
      'document already used': this.localization.instant("::DocumentAlreadyUsed"),
      'unitNameAlreadyUsed': this.localization.instant("::UnitNameAlreadyUsed"),
      "areaNameAlreadyUsed": this.localization.instant("::AreaNameAlreadyUsed"),
      "Could not find a valid instructor": this.localization.instant("::CouldNotFindAnInstructor"),
      "The instructor has already the same training as attendee.": this.localization.instant("::InstructorAlreadyAsAttendee"),
      "The instructor has already the same training assigned.": this.localization.instant("::InstructorAlreadyRegistered"),
      "topicNameAlreadyUsed": this.localization.instant("::TopicNameAlreadyUsed"),
      "already has a valid account as instructor": this.localization.instant("::AlreadyHasAValidAccountAsInstructor"),
      "TrainingTemplateNameAlreadyUsed": this.localization.instant("::TrainingTemplateNameAlreadyUsed"),
      "topicHasRelatedTrainings": this.localization.instant("::DeleteTopicWarningMessage"),
      "unitHasRelatedAreas": this.localization.instant("::DeleteUnitWarningMessage"),
      "trainingTemplateUsedInCurrentTraining": this.localization.instant("::TrainingTemplateUsedInCurrentTraining"),
      "AreaUsedInCurrentTraining": this.localization.instant("::AreaUsedInCurrentTraining"),
      "UnitUsedInCurrentTraining": this.localization.instant("::UnitUsedInCurrentTraining"),
      "TopicUsedInCurrentTraining": this.localization.instant("::TopicUsedInCurrentTraining"),
      "endDateCantBeEarlier": this.localization.instant("::EndDateCantBeEarlier"),
      [CONCURRENCY_ERROR_FRAGMENT]: this.localization.instant("::RecordChangedElsewhere")
      //Map more error messages
    };

    for (const key in errorMap) {
      if (message.toLowerCase().includes(key.toLowerCase())) {
        return errorMap[key]; // return message if coincidence is found
      }
    }
    //default message if there is no coincidence
    return message
  }
}
